/**
 * WelcomeDialog — three first steps, shown once, on someone's first run.
 *
 * "First run" is the What's New popover's own signal: no version acknowledged
 * yet in this browser. So people who have used the app before never see it,
 * and a brand-new user gets this INSTEAD of a release note about changes to a
 * product they have not seen. Closing it acknowledges the current version, so
 * the What's New popover stays quiet until the next release.
 */

import { Link } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { HandWave24Regular } from '@fluentui/react-icons'
import { CURRENT_VERSION } from '../data/whatsNew'
import { WELCOME_STEPS, type OnboardingKind } from '../lib/onboarding'
import { writeSeen } from './WhatsNewDialog'

const useStyles = makeStyles({
  title: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  icon: { color: '#1f5bb5' },
  list: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  detail: { color: tokens.colorNeutralForeground2 },
})

export function WelcomeDialog({ kind, onClose }: { kind: OnboardingKind; onClose: () => void }) {
  const styles = useStyles()

  function close() {
    writeSeen(CURRENT_VERSION)
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(_, d) => {
        if (!d.open) close()
      }}
    >
      <DialogSurface data-testid="welcome-dialog">
        <DialogBody>
          <DialogTitle className={styles.title}>
            <HandWave24Regular className={styles.icon} aria-hidden />
            Welcome to DACcelerate
          </DialogTitle>
          <DialogContent>
            <ol className={styles.list}>
              {WELCOME_STEPS[kind].map((s, i) => (
                <li key={s.title} data-testid={`welcome-step-${i}`}>
                  <strong>{s.title}</strong> <span className={styles.detail}>{s.detail}</span>{' '}
                  <Link to={`/help#${s.help}`} onClick={close}>
                    How
                  </Link>
                </li>
              ))}
            </ol>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={close} data-testid="welcome-dismiss">
              Get started
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
